import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import ModalFooter from '../common/ModalFooter';
import DatePicker from '../common/DatePicker';
import Dropdown from '../common/Dropdown';
import FileUploader from '../common/FileUploader';
import InputField from '../common/InputField';
import Modal from '../common/Modal';
import FormField from '../common/FormField';

const ImportPriceListModal: React.FC<{
  open: boolean;
  onClose: () => void;
  handleSubmit: () => void;
  control: any;
  errors?: any;
  customerOptions?: { value: string | number; label: string }[];
  supplierOptions?: { value: string | number; label: string }[];
}> = ({
  open,
  onClose,
  handleSubmit,
  control,
  errors,
  customerOptions = [],
  supplierOptions = [],
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('priceList.importPriceList')}
      size="md"
      footer={
        <ModalFooter onCancel={onClose} onSubmit={handleSubmit} submitText={t('buttons.import')} />
      }
    >
      <Grid container pt={3}>
        <Grid size={{ xs: 12, md: 12 }}>
          <FormField label={t('form.customer')} required>
            <Controller
              name="customer"
              control={control}
              rules={{ required: t('form.customer') + ' is required' }}
              render={({ field }: any) => (
                <Dropdown
                  {...field}
                  value={field?.value ?? ''}
                  variant="outlined"
                  fullWidth
                  options={customerOptions}
                  error={!!errors?.customer}
                  helperText={errors?.customer?.message as string}
                />
              )}
            />
          </FormField>
        </Grid>
        <Grid size={{ xs: 12, md: 12 }}>
          <FormField label={t('form.supplier')} required>
            <Controller
              name="supplier"
              control={control}
              rules={{ required: t('form.supplier') + ' is required' }}
              render={({ field }: any) => (
                <Dropdown
                  {...field}
                  value={field?.value ?? ''}
                  variant="outlined"
                  fullWidth
                  options={supplierOptions}
                  error={!!errors?.supplier}
                  helperText={errors?.supplier?.message as string}
                />
              )}
            />
          </FormField>
        </Grid>
        <Grid size={{ xs: 12, md: 12 }}>
          <FormField label={t('form.currency')}>
            <InputField value={'USD'} variant="outlined" fullWidth disabled />
          </FormField>
        </Grid>
        <Grid size={{ xs: 12, md: 12 }}>
          <FormField label={t('form.validityDateRange')} required>
            <Controller
              name="validityDateRange"
              control={control}
              rules={{
                required: t('form.validityDateRange') + ' is required',
                validate: (value) => {
                  if (!value || !value.startDate) {
                    return t('form.selectValidDateRange');
                  }
                  return true;
                },
              }}
              render={({ field }: any) => (
                <>
                  <DatePicker
                    showValueInField
                    value={field?.value}
                    onApply={(range: any) => field.onChange(range)}
                    textFieldSx={{
                      border: `1px solid ${
                        errors?.validityDateRange ? 'red' : theme.palette.common.white
                      }`,
                      borderRadius: `${theme.borderRadius.md}px`,
                      '& .MuiOutlinedInput-input': {
                        padding: '10px 8px',
                        fontSize: '14px',
                        cursor: 'pointer',
                      },
                    }}
                    iconSx={{
                      padding: '10px 8px',
                    }}
                  />
                  {errors?.validityDateRange && (
                    <Box sx={{ color: 'red', fontSize: '12px', mt: 0.5 }}>
                      {errors?.validityDateRange?.message as string}
                    </Box>
                  )}
                </>
              )}
            />
          </FormField>
        </Grid>

        <Grid size={{ xs: 12, md: 12 }}>
          <FormField label={t('form.documentId')} required>
            <Controller
              name="documentId"
              control={control}
              rules={{ required: t('form.documentId') + ' is required' }}
              render={({ field }: any) => (
                <InputField
                  {...field}
                  value={field?.value ?? ''}
                  variant="outlined"
                  fullWidth
                  error={!!errors?.documentId}
                  helperText={errors?.documentId?.message as string}
                />
              )}
            />
          </FormField>
        </Grid>
        <Grid size={{ xs: 12, md: 12 }}>
          <FormField label={t('form.file')} required>
            <Controller
              name="file"
              control={control}
              rules={{
                required: t('form.file') + ' is required',
                validate: (value) => {
                  if (!value || (Array.isArray(value) && value.length === 0)) {
                    return t('form.uploadFile');
                  }
                  return true;
                },
              }}
              render={({ field }: any) => (
                <>
                  <FileUploader value={field?.value} onChange={field.onChange} />
                  {errors?.file && (
                    <Box sx={{ color: 'red', fontSize: '12px', mt: 0.5 }}>
                      {errors?.file?.message as string}
                    </Box>
                  )}
                </>
              )}
            />
          </FormField>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default ImportPriceListModal;
